/* Role adaptation — which sections of the app this company gets.
 *
 * The list arrives per membership in `/me` (`capabilities.surfaces`) and is
 * computed by `apps.tenants.surfaces` on the backend: role default, plus
 * anything the company already has data behind, minus/plus whatever an
 * operator pinned in Backoffice → Roles & surfaces. One answer, one place.
 *
 * A shop is the end of the chain: it has suppliers and no clients, prices
 * what it bought against what it sells it for, and never fulfils an order.
 * So its «Мои клиенты» tab, the client price matrix and the seller half of
 * the order flow are simply not drawn — not disabled, not empty: absent.
 *
 * Surfaces are about the *product*, permissions are about the *user*. Both
 * gates still apply: `can()` decides whether this member may open a section
 * the company has.
 */
import type { AuthState } from "./auth";

/** Ids mirror `apps.tenants.surfaces.SURFACES`. */
export type SurfaceId =
  | `nav.${string}`
  | `partners.${string}`
  | `prices.${string}`
  | `orders.${string}`;

function surfacesOf(auth: AuthState, slug: string): string[] | null {
  const list = auth.memberships?.find((m) => m.tenant.slug === slug)?.capabilities?.surfaces;
  return Array.isArray(list) ? list : null;
}

/** Does this company get that section?
 *
 * A session whose `/me` predates surfaces (an old cached payload) returns
 * `true` for everything: the failure mode of a missing list must be the app
 * as it was, never a sidebar that silently loses half its entries.
 */
export function hasSurface(auth: AuthState, slug: string, id: SurfaceId): boolean {
  const list = surfacesOf(auth, slug);
  return list === null ? true : list.includes(id);
}

/** Keep only the ids this company has, preserving the caller's order. */
export function filterSurfaces<T>(
  auth: AuthState,
  slug: string,
  items: T[],
  idOf: (item: T) => SurfaceId | null,
): T[] {
  const list = surfacesOf(auth, slug);
  if (list === null) return items;
  return items.filter((item) => {
    const id = idOf(item);
    return id === null || list.includes(id);
  });
}

/** The page a signed-in company opens on. A company in the short workspace
 *  (an invited client that chose «только заказы» / «возможности системы»)
 *  has no dashboard — its first screen is its orders. */
export function homePath(auth: AuthState, slug: string): string {
  return hasSurface(auth, slug, "nav.dashboard") ? `/t/${slug}/dashboard` : `/t/${slug}/orders`;
}

/** "orders" / "explore" for the short workspace, "" for the full one. */
export function workspaceOf(auth: AuthState, slug: string): string {
  return auth.memberships?.find((m) => m.tenant.slug === slug)?.tenant.workspace ?? "";
}
